import { forwardRef, useImperativeHandle, useRef, useState } from 'react';

import { Button, Checkbox } from 'antd';
import { Loader } from 'lucide-react';

import CommonDialog from '@/components/commonDialog';
import { config } from '@/pages/assistance_requests/consent/config';
import useGlobalConfig from '@/store/global';

const ConsentDialog = forwardRef<any, any>((props, ref) => {
  const { program, themeColor } = useGlobalConfig();
  const consentConfig = config[program.name as keyof typeof config];
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dialogRef = useRef<any>(null);

  const handleSubmit = async () => {
    if (!isChecked) return;

    setIsSubmitting(true);
    try {
      props.submit();
      dialogRef?.current?.closeDialog();
      setIsChecked(false);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useImperativeHandle(ref, () => ({
    open: () => dialogRef?.current?.openDialog(),
    close: () => dialogRef?.current?.closeDialog(),
  }));

  return (
    <CommonDialog
      ref={dialogRef}
      width={800}
      title="Confirmation of Patient Consent for Copay Assistance"
      cancelButton={false}
    >
      <div className="mx-auto max-w-4xl">
        <div>
          <p className="mb-4 text-sm text-gray-500">
            Before proceeding with the claim submission, we need you to confirm that you have
            obtained the patient’s consent to ensure compliance with privacy regulations and to
            protect the patient’s rights.
          </p>
        </div>
        <div className="mt-6">
          <h2 className="text-base font-bold">What am I confirming?</h2>
          <ul className="mt-3 grid list-disc gap-y-2 pl-6 text-sm">
            <li>
              The patient wants to receive copay assistance from Octapharma USA, Inc.
              (“Octapharma”).
            </li>
            <li>
              You have informed the patient about the purpose of collecting their health
              information.
            </li>
            <li>
              You have explained how their information will be used and who it may be shared with.
            </li>
            <li>
              You have obtained the patient’s explicit written consent to submit this request for
              copay assistance and share their health information as necessary.
            </li>
            <li>
              The patient understands their rights regarding their health information, including the
              right to access, correct, or withdraw consent.
            </li>
            <li>
              The patient understands their personal data related to their redemption of copay
              assistance may be collected, analyzed, and shared with Octapharma for market research
              and other purposes related to assessing Octapharma’s programs. Any such personal data
              shared with Octapharma will be aggregated and de-identified; it will be combined with
              data related to other copay card redemptions and will not identify the patient.
            </li>
            <li>You have documented this consent in the patient’s records.</li>
            <li>
              You have reviewed the program Terms & Conditions with the patient, including that the
              patient:
            </li>
            <ul className="mb-10 grid list-disc gap-y-2 pl-6">
              <li>Wants to utilize the copay assistance program from Octapharma.</li>
              <li>Has and will maintain private health insurance coverage.</li>
              <li>
                Understands they are not eligible for the program if they are a beneficiary of any
                government sponsored healthcare program, including but not limited to Medicare,
                Medicaid, Medigap, VA, DOD, Tricare, or any other federal or state government health
                insurance.
              </li>
              <li>
                Understands that the maximum benefit is limited to up to {consentConfig.limit} per
                calendar year and agrees not to use the assistance if their insurer prohibits it.
              </li>
              <li>
                Understands that the program does NOT cover costs associated with therapy
                administration, such as office visits or infusion administration costs.
              </li>
              <li>Acknowledges that this copay assistance is void where prohibited by law.</li>
              <li>
                Understands that Octapharma reserves the right to rescind, revoke or amend this
                offer and the copay assistance program without notice.
              </li>
              <li>
                Understands that patients with insurance plans or employers participating in an
                alternate funding program (also sometimes referred to as patient advocacy programs,
                specialty networks, SHARx, Paydhealth, or Payer Matrix, among other names) requiring
                them to apply to a manufacturer’s patient assistance program or otherwise pursue
                specialty drug prescription coverage through an alternate funding vendor as a
                condition of, requirement for, or prerequisite to coverage of relevant Octapharma
                products, or that otherwise denies, restricts, eliminates, delays, alters, or
                withholds any insurance benefits or coverage contingent upon application to, or
                denial of eligibility for, specialty drug prescription coverage through the
                alternate funding program are not eligible. The patient further agrees to inform
                Octapharma if they are a member of such an insurance plan.
              </li>
            </ul>
          </ul>
          <div className="space-y-10 text-base">
            <div className="">
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="consent"
                  checked={isChecked}
                  onChange={(e) => setIsChecked(e.target.checked)}
                />
                <label
                  htmlFor="consent"
                  className="font-semibold leading-tight peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  By checking this box, you hereby certify that all the statements above are true
                  and correct.
                </label>
              </div>
              <p className="pl-6 text-sm">
                For more information, please call {consentConfig?.phone}
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-stretch space-y-4">
          <Button
            style={{
              backgroundColor: themeColor,
              height: '40px',
              color: '#fff',
            }}
            loading={isSubmitting}
            onClick={handleSubmit}
            disabled={!isChecked || isSubmitting}
            className="mt-6 disabled:opacity-50"
          >
            {isSubmitting ? <Loader className="h-6 w-6 animate-spin" /> : 'Submit'}
          </Button>
        </div>
      </div>
    </CommonDialog>
  );
});

ConsentDialog.displayName = 'ConsentDialog';

export default ConsentDialog;
